import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import type { StaatidTrendPoint } from './api'
import { formatDager, formatMonthName } from './api'
import { isPartialMonth, formatNo } from '../partialPeriod'

/** Up to 24 months on one chart (#529 AC5). A longer period keeps the most
 *  recent 24 and says so — the x-axis stops being readable past that. */
export const MAX_MONTHS = 24

/** Colour of the median line. Matches the cyan the rest of Ståtid uses. */
const MEDIAN_COLOR = '#06b6d4'

/** "YYYY-MM-DD" (or "YYYY-MM") -> [year, month]. */
function ym(dateStr: string): [number, number] {
  const [y, m] = dateStr.split('-').map(Number)
  return [y, m]
}

/**
 * One point per calendar month between `from` and `to`, inclusive (#529 AC1).
 *
 * The backend only emits months that actually had a sale. Feeding that straight
 * into a line chart draws a segment from March to June as if nothing happened
 * in between — a gap rendered as continuity. Months with no data come back with
 * `antall: 0` and null medians instead, and the line breaks there.
 *
 * A period longer than MAX_MONTHS keeps the most recent MAX_MONTHS months.
 */
export function fillCalendarMonths(
  trend: StaatidTrendPoint[],
  from: string,
  to: string,
): StaatidTrendPoint[] {
  const byMonth = new Map(trend.map(t => [t.maned, t]))
  const out: StaatidTrendPoint[] = []
  let [y, m] = ym(from)
  const [endY, endM] = ym(to)
  while (y < endY || (y === endY && m <= endM)) {
    const maned = `${y}-${String(m).padStart(2, '0')}`
    out.push(
      byMonth.get(maned) ?? { maned, antall: 0, median_dager: null, snitt_dager: null },
    )
    m += 1
    if (m === 13) { y += 1; m = 1 }
  }
  return out.slice(-MAX_MONTHS)
}

interface Props {
  /** Monthly points as the backend returned them — sparse is fine. */
  trend: StaatidTrendPoint[]
  /** Period bounds ("YYYY-MM-DD"), so empty months become real gaps. */
  from: string
  to: string
  loading: boolean
  title?: string
  /** What the numbers are about, e.g. the dealer's name — shown in the header. */
  scope?: string | null
  /** Newest date the data covers ("YYYY-MM-DD"), from useMaxDataDate. */
  maxDataDate?: string | null
  /** Month table under the chart. Default true. ForhandlerDetalj turns it off
   *  (#655): the tooltip carries the same numbers. */
  showTable?: boolean
}

const fmtAntall = (n: number) => n.toLocaleString('nb-NO')

/** Legend entry: an explicit label for every quantity on screen, plotted or
 *  not (#529 AC4) — reading the chart must not depend on decoding a colour. */
function LegendItem({ swatch, label, hint }: { swatch?: string; label: string; hint: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {swatch ? (
        <span
          className="inline-block w-4 h-0.5 rounded-sm"
          style={{ backgroundColor: swatch }}
          aria-hidden
        />
      ) : (
        <span className="inline-block w-4 border-t border-dashed border-gray-400" aria-hidden />
      )}
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className="text-gray-500 dark:text-gray-400">— {hint}</span>
    </span>
  )
}

/**
 * Month-by-month ståtid for whatever the filters select (#529).
 *
 * The line plots the **median**, not the mean: ståtid is right-skewed, and a
 * handful of cars sitting 300+ days pulls the mean well above what a typical
 * car experiences. The mean is still reported per month — in the tooltip and
 * the table — it just isn't a second line competing with the median.
 *
 * The sale count is a number per month, never a plotted series: a second
 * y-axis in a different unit is the thing that made the old trend chart hard
 * to read, so this one keeps a single scale, in days (#529 AC3).
 */
export default function MonthlyTrendChart({
  trend, from, to, loading, title, scope, maxDataDate,
  showTable = true,
}: Props) {
  const { t } = useTranslation('staatid')
  const data = useMemo(() => fillCalendarMonths(trend, from, to), [trend, from, to])
  const truncated = monthSpan(from, to) > MAX_MONTHS
  // The nightly import leaves the current month incomplete, so its antall is
  // not comparable to the months beside it and its median rests on fewer sales.
  const partial = data.find(p => isPartialMonth(p.maned, maxDataDate))

  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="chart-skeleton" className="animate-pulse h-64 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  const medianPoints = data.filter(d => d.median_dager !== null).length

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TooltipContent = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null
    const p = payload[0].payload as StaatidTrendPoint
    return (
      <div
        data-testid="staatid-monthly-trend-tooltip"
        className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-sm shadow-xs px-3 py-2 text-xs space-y-0.5"
      >
        <p className="font-medium text-gray-900 dark:text-gray-100">{t('common.maanedValue', { maned: p.maned })}</p>
        <p>{t('common.stat.median')} <strong>{formatDager(p.median_dager)} d</strong></p>
        <p>{t('common.stat.snitt')} <strong>{formatDager(p.snitt_dager ?? null)} d</strong></p>
        <p>{t('common.stat.antallSluttsalg')} <strong>{fmtAntall(p.antall)}</strong></p>
        {isPartialMonth(p.maned, maxDataDate) && (
          <p className="text-amber-600 dark:text-amber-400">{t('monthly.partialMonth')}</p>
        )}
      </div>
    )
  }

  return (
    <div className="card p-4" data-testid="staatid-monthly-trend">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {title ?? t('monthly.title')}{scope ? ` — ${scope}` : ''}
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {data.length === 1 ? t('monthly.monthsOne', { n: data.length }) : t('monthly.monthsMany', { n: data.length })}
        </span>
      </div>

      <div
        data-testid="staatid-monthly-trend-legend"
        className="flex flex-wrap gap-x-5 gap-y-1 mb-3"
      >
        <LegendItem label={t('common.maaned')} hint={t('monthly.legendMaanedHint')} />
        <LegendItem swatch={MEDIAN_COLOR} label={t('common.medianDager')} hint={t('monthly.legendMedianHint')} />
        <LegendItem
          label={t('common.snittDager')}
          hint={showTable ? t('monthly.legendSnittHintTable') : t('monthly.legendSnittHintTooltip')}
        />
        <LegendItem
          label={t('common.antallSluttsalg')}
          hint={showTable ? t('monthly.legendAntallHintTable') : t('monthly.legendAntallHintTooltip')}
        />
      </div>

      {medianPoints === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('monthly.noPaired')}</p>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="maned" tick={{ fontSize: 11 }} />
            {/* One axis, one unit. The sale count lives in the table below. */}
            <YAxis tick={{ fontSize: 11 }} unit=" d" allowDecimals={false} />
            <Tooltip content={<TooltipContent />} />
            <Line
              type="monotone"
              dataKey="median_dager"
              name={t('common.medianDager')}
              stroke={MEDIAN_COLOR}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              // A month with no sales must break the line, not be bridged.
              connectNulls={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}

      {partial && maxDataDate && (
        <p
          className="text-xs text-gray-500 dark:text-gray-400 mt-2"
          data-testid="staatid-monthly-trend-partial-note"
        >
          <Trans
            t={t}
            i18nKey="monthly.partialNote"
            values={{ month: formatMonthName(partial.maned), date: formatNo(maxDataDate) }}
            components={{ b: <strong /> }}
          />
        </p>
      )}

      {truncated && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          {t('monthly.truncated', { max: MAX_MONTHS })}
        </p>
      )}

      {showTable && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-testid="staatid-monthly-trend-table">
            <thead>
              <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
                <th className="px-3 py-1.5 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.maaned')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antallSluttsalg')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
                <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p, i) => (
                <tr
                  key={p.maned}
                  data-testid="staatid-monthly-trend-row"
                  className={`border-b border-gray-100 dark:border-dark-border ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                >
                  <td className="px-3 py-1.5">
                    {p.maned}
                    {isPartialMonth(p.maned, maxDataDate) && (
                      <span
                        data-testid="staatid-monthly-trend-partial-badge"
                        className="ml-2 text-xs text-amber-600 dark:text-amber-400"
                      >
                        {t('monthly.partialBadge')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtAntall(p.antall)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatDager(p.median_dager)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatDager(p.snitt_dager ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Months between two "YYYY-MM-DD" bounds, inclusive — used only to tell
 *  whether MAX_MONTHS actually cut anything off. */
function monthSpan(from: string, to: string): number {
  const [fy, fm] = ym(from)
  const [ty, tm] = ym(to)
  return (ty - fy) * 12 + (tm - fm) + 1
}
